import PageLayout from "@/components/PageLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { trackIntegrationView } from "@/lib/analytics";
import { Search, ArrowRight, Star, Zap, ShoppingCart, Package, TrendingUp, Mail, Headphones, Truck, Grid, DollarSign, Building2, Users, SlidersHorizontal } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

// Import integration logos
import shopifyLogo from "@/assets/integrations/shopify.svg";
import bigcommerceLogo from "@/assets/integrations/bigcommerce.svg";
import woocommerceLogo from "@/assets/integrations/woocommerce.svg";
import magentoLogo from "@/assets/integrations/magento.svg";
import adobecommerceLogo from "@/assets/integrations/adobecommerce.svg";
import commercetoolsLogo from "@/assets/integrations/commercetools.svg";
import salesforceLogo from "@/assets/integrations/salesforce.svg";
import sapLogo from "@/assets/integrations/sap.svg";
import cegidLogo from "@/assets/integrations/cegid.svg";
import newstoreLogo from "@/assets/integrations/newstore.svg";
import marketplacerLogo from "@/assets/integrations/marketplacer.svg";
import miraklLogo from "@/assets/integrations/mirakl.svg";
import channelengineLogo from "@/assets/integrations/channelengine.svg";
import klaviyoLogo from "@/assets/integrations/klaviyo.svg";
import emarsysLogo from "@/assets/integrations/emarsys.svg";
import algoliaLogo from "@/assets/integrations/algolia.svg";
import reviewsLogo from "@/assets/integrations/reviews.svg";
import gorgiasLogo from "@/assets/integrations/gorgias.svg";
import zendeskLogo from "@/assets/integrations/zendesk.svg";
import shipbobLogo from "@/assets/integrations/shipbob.svg";
import airtableLogo from "@/assets/integrations/airtable.svg";
import notionLogo from "@/assets/integrations/notion.svg";
import googlesheetsLogo from "@/assets/integrations/googlesheets.svg";
import xeroLogo from "@/assets/integrations/xero.svg";
import netsuiteLogo from "@/assets/integrations/netsuite.svg";
import odooLogo from "@/assets/integrations/odoo.svg";
import fluentCommerceLogo from "@/assets/integrations/fluent-commerce.svg";
import googlePubsubLogo from "@/assets/integrations/google-pubsub.svg";
import returnlessLogo from "@/assets/integrations/returnless.svg";
import sanityLogo from "@/assets/integrations/sanity.svg";
import cin7Logo from "@/assets/integrations/cin7.svg";
import rithumLogo from "@/assets/integrations/rithum.svg";
import lightspeedLogo from "@/assets/integrations/lightspeed.svg";
import loopReturnsLogo from "@/assets/integrations/loop-returns.avif";
import ebayLogo from "@/assets/integrations/ebay.webp";
import peoplevoxLogo from "@/assets/integrations/peoplevox.svg";
import sekoLogo from "@/assets/integrations/seko.svg";
import hubspot2Logo from "@/assets/integrations/hubspot-2.svg";
import openaiLogo from "@/assets/integrations/openai.svg";
import bloomreachLogo from "@/assets/integrations/bloomreach.svg";
import mongodbLogo from "@/assets/integrations/mongodb.svg";
import bigqueryLogo from "@/assets/integrations/bigquery.svg";
import rabbitmqLogo from "@/assets/integrations/rabbitmq.svg";
import medusaLogo from "@/assets/integrations/medusa.png";
import retailExpressLogo from "@/assets/integrations/retail-express.svg";

const Integrations = () => {
  useSEO({
    title: '100+ Commerce Platform Integrations | Ommni - Connect Everything',
    description: 'Connect Ommni with Shopify, Magento, BigCommerce, SAP, NetSuite, and 100+ e-commerce, ERP, CRM, and marketplace platforms. Built by experts, maintained by Ommni.',
    keywords: 'commerce integrations, API integrations, Shopify integration, Magento integration, ERP integration, marketplace integration, ecommerce connectors',
    canonical: 'https://ommni.io/integrations',
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [selectedBuilder, setSelectedBuilder] = useState<string>("All");
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const categoryIcons: Record<string, any> = {
    "E-commerce": ShoppingCart,
    "CRM": TrendingUp,
    "ERP": Grid,
    "POS": Package,
    "OMS": Building2,
    "Marketplace": TrendingUp,
    "Marketing": Mail,
    "Search": Search,
    "Reviews": Star,
    "Support": Headphones,
    "Fulfillment": Truck,
    "Productivity": Grid,
    "Accounting": DollarSign,
    "Returns": Package,
    "CMS": Grid,
    "Database": Grid,
    "Analytics": TrendingUp,
    "AI": Zap,
  };

  const integrations = [
    {
      name: "Shopify",
      category: ["E-commerce"],
      description: "Sync products, orders, and inventory with Shopify stores in real-time",
      logo: shopifyLogo,
      popular: true,
      rating: 4.8,
      link: "/integrations/shopify",
      builtBy: "Ommni",
    },
    {
      name: "BigCommerce",
      category: ["E-commerce"],
      description: "Connect your BigCommerce store for seamless multi-channel commerce",
      logo: bigcommerceLogo,
      popular: true,
      rating: 4.7,
      builtBy: "Ommni",
    },
    {
      name: "WooCommerce",
      category: ["E-commerce"],
      description: "Integrate WordPress WooCommerce stores with your operations",
      logo: woocommerceLogo,
      rating: 4.6,
      builtBy: "Ommni",
    },
    {
      name: "Magento",
      category: ["E-commerce"],
      description: "Real-time sync with Magento Adobe Commerce platform",
      logo: magentoLogo,
      rating: 4.5,
      builtBy: "Partner",
    },
    {
      name: "Adobe Commerce",
      category: ["E-commerce"],
      description: "Enterprise-grade Adobe Commerce (Magento) integration",
      logo: adobecommerceLogo,
      rating: 4.6,
      builtBy: "Ommni",
    },
    {
      name: "commercetools",
      category: ["E-commerce"],
      description: "Headless commerce platform for modern retail experiences",
      logo: commercetoolsLogo,
      rating: 4.7,
      builtBy: "Partner",
    },
    {
      name: "Medusa",
      category: ["E-commerce"],
      description: "Open-source headless commerce engine for digital commerce experiences",
      logo: medusaLogo,
      rating: 4.6,
      builtBy: "Partner",
    },
    {
      name: "Salesforce",
      category: ["CRM"],
      description: "Connect customer data with your commerce operations",
      logo: salesforceLogo,
      popular: true,
      rating: 4.8,
      builtBy: "Ommni",
    },
    {
      name: "SAP",
      category: ["ERP"],
      description: "Enterprise resource planning integration for large operations",
      logo: sapLogo,
      rating: 4.5,
      builtBy: "Partner",
    },
    {
      name: "NetSuite",
      category: ["ERP"],
      description: "Cloud-based ERP solution for financial management and operations",
      logo: netsuiteLogo,
      popular: true,
      rating: 4.7,
      builtBy: "Ommni",
    },
    {
      name: "Odoo",
      category: ["ERP"],
      description: "Open-source business management suite with comprehensive modules",
      logo: odooLogo,
      rating: 4.6,
      builtBy: "Partner",
    },
    {
      name: "Cegid",
      category: ["ERP"],
      description: "Retail management software integration for unified operations",
      logo: cegidLogo,
      rating: 4.4,
      builtBy: "Partner",
    },
    {
      name: "NewStore",
      category: ["POS", "OMS"],
      description: "Mobile-first omnichannel commerce platform integration",
      logo: newstoreLogo,
      rating: 4.6,
      builtBy: "Partner",
    },
    {
      name: "Marketplacer",
      category: ["Marketplace"],
      description: "Build and manage your own marketplace with ease",
      logo: marketplacerLogo,
      rating: 4.5,
      builtBy: "Partner",
    },
    {
      name: "Mirakl",
      category: ["Marketplace"],
      description: "Enterprise marketplace platform for B2B and B2C",
      logo: miraklLogo,
      rating: 4.6,
      builtBy: "Partner",
    },
    {
      name: "ChannelEngine",
      category: ["Marketplace"],
      description: "Connect to 950+ marketplaces and retailers worldwide",
      logo: channelengineLogo,
      popular: true,
      rating: 4.7,
      builtBy: "Ommni",
    },
    {
      name: "Klaviyo",
      category: ["Marketing"],
      description: "Email marketing and customer data platform integration",
      logo: klaviyoLogo,
      rating: 4.8,
      builtBy: "Ommni",
    },
    {
      name: "Emarsys",
      category: ["Marketing"],
      description: "Omnichannel customer engagement platform",
      logo: emarsysLogo,
      rating: 4.5,
      builtBy: "Partner",
    },
    {
      name: "Algolia",
      category: ["Search"],
      description: "AI-powered search and discovery for commerce",
      logo: algoliaLogo,
      rating: 4.7,
      builtBy: "Partner",
    },
    {
      name: "Reviews.io",
      category: ["Reviews"],
      description: "Customer reviews and ratings platform integration",
      logo: reviewsLogo,
      rating: 4.6,
      builtBy: "Partner",
    },
    {
      name: "Gorgias",
      category: ["Support"],
      description: "Customer support helpdesk for e-commerce teams",
      logo: gorgiasLogo,
      rating: 4.7,
      builtBy: "Partner",
    },
    {
      name: "Zendesk",
      category: ["Support"],
      description: "Customer service and engagement platform",
      logo: zendeskLogo,
      rating: 4.6,
      builtBy: "Partner",
    },
    {
      name: "ShipBob",
      category: ["Fulfillment"],
      description: "3PL fulfillment and logistics partner integration",
      logo: shipbobLogo,
      rating: 4.5,
      builtBy: "Partner",
    },
    {
      name: "Airtable",
      category: ["Productivity"],
      description: "Low-code platform for building collaborative apps",
      logo: airtableLogo,
      rating: 4.6,
      builtBy: "Partner",
    },
    {
      name: "Notion",
      category: ["Productivity"],
      description: "All-in-one workspace for notes and collaboration",
      logo: notionLogo,
      rating: 4.7,
      builtBy: "Partner",
    },
    {
      name: "Google Sheets",
      category: ["Productivity"],
      description: "Sync data with Google Sheets for reporting and analysis",
      logo: googlesheetsLogo,
      rating: 4.5,
      builtBy: "Ommni",
    },
    {
      name: "Google Shopping",
      category: ["Marketplace"],
      description: "Elevate your presence on Google Shopping with automated product feeds",
      logo: googlesheetsLogo,
      popular: true,
      rating: 4.8,
      link: "/integrations/google-shopping",
      builtBy: "Ommni",
    },
    {
      name: "Xero",
      category: ["Accounting"],
      description: "Cloud accounting software for small businesses",
      logo: xeroLogo,
      rating: 4.6,
      builtBy: "Partner",
    },
    {
      name: "Fluent Commerce",
      category: ["E-commerce"],
      description: "Order management system for omnichannel retail operations",
      logo: fluentCommerceLogo,
      rating: 4.7,
      builtBy: "Partner",
    },
    {
      name: "Google Pub/Sub",
      category: ["Productivity"],
      description: "Real-time messaging service for event-driven systems",
      logo: googlePubsubLogo,
      rating: 4.5,
      builtBy: "Partner",
    },
    {
      name: "Returnless",
      category: ["Returns"],
      description: "Smart returns management and automation platform",
      logo: returnlessLogo,
      rating: 4.6,
      builtBy: "Partner",
    },
    {
      name: "Sanity",
      category: ["CMS"],
      description: "Headless CMS platform for structured content management",
      logo: sanityLogo,
      rating: 4.7,
      builtBy: "Partner",
    },
    {
      name: "Cin7",
      category: ["ERP"],
      description: "Inventory management and order fulfillment platform",
      logo: cin7Logo,
      rating: 4.5,
      builtBy: "Partner",
    },
    {
      name: "Rithum",
      category: ["Marketplace"],
      description: "Multi-channel commerce and marketplace integration platform",
      logo: rithumLogo,
      rating: 4.6,
      builtBy: "Partner",
    },
    {
      name: "Lightspeed",
      category: ["POS", "ERP"],
      description: "Cloud-based POS and retail management solution",
      logo: lightspeedLogo,
      rating: 4.7,
      builtBy: "Partner",
    },
    {
      name: "Loop Returns",
      category: ["Returns"],
      description: "Returns and exchanges platform for e-commerce brands",
      logo: loopReturnsLogo,
      rating: 4.8,
      builtBy: "Partner",
    },
    {
      name: "eBay",
      category: ["Marketplace"],
      description: "Global online marketplace for buying and selling products",
      logo: ebayLogo,
      popular: true,
      rating: 4.7,
      builtBy: "Ommni",
    },
    {
      name: "Peoplevox",
      category: ["Fulfillment"],
      description: "Cloud-based warehouse management system for e-commerce",
      logo: peoplevoxLogo,
      rating: 4.6,
      builtBy: "Partner",
    },
    {
      name: "SEKO Logistics",
      category: ["Fulfillment"],
      description: "Global logistics and supply chain solutions provider",
      logo: sekoLogo,
      rating: 4.5,
      builtBy: "Partner",
    },
    {
      name: "HubSpot",
      category: ["CRM"],
      description: "All-in-one CRM platform for marketing, sales, and service",
      logo: hubspot2Logo,
      popular: true,
      rating: 4.8,
      builtBy: "Ommni",
    },
    {
      name: "OpenAI",
      category: ["AI"],
      description: "AI-powered language models and automation tools",
      logo: openaiLogo,
      popular: true,
      rating: 4.9,
      builtBy: "Ommni",
    },
    {
      name: "Bloomreach",
      category: ["Marketing"],
      description: "Commerce experience cloud for personalization and discovery",
      logo: bloomreachLogo,
      rating: 4.6,
      builtBy: "Partner",
    },
    {
      name: "MongoDB",
      category: ["Database"],
      description: "NoSQL database platform for modern applications",
      logo: mongodbLogo,
      rating: 4.7,
      builtBy: "Partner",
    },
    {
      name: "BigQuery",
      category: ["Analytics"],
      description: "Google Cloud's serverless data warehouse for analytics",
      logo: bigqueryLogo,
      rating: 4.8,
      builtBy: "Partner",
    },
    {
      name: "RabbitMQ",
      category: ["Productivity"],
      description: "Open-source message broker for reliable messaging",
      logo: rabbitmqLogo,
      rating: 4.5,
      builtBy: "Partner",
    },
    {
      name: "Retail Express",
      category: ["POS", "ERP"],
      description: "Comprehensive retail management platform for point-of-sale and inventory",
      logo: retailExpressLogo,
      rating: 4.6,
      builtBy: "Partner",
    },
  ];

  const filteredIntegrations = integrations.filter((integration) => {
    const matchesSearch = integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      integration.category.some(cat => cat.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === "All" || integration.category.includes(selectedCategory);
    const matchesBuilder = selectedBuilder === "All" || integration.builtBy === selectedBuilder;
    return matchesSearch && matchesCategory && matchesBuilder;
  });

  const categories = [...new Set(integrations.flatMap((i) => i.category))];
  const categoryCounts = categories.reduce((acc, cat) => {
    acc[cat] = integrations.filter(i => i.category.includes(cat)).length;
    return acc;
  }, {} as Record<string, number>);
  
  const ommniCount = integrations.filter(i => i.builtBy === "Ommni").length;
  const partnerCount = integrations.filter(i => i.builtBy === "Partner").length;

  return (
    <PageLayout>
      <div className="px-4 pb-24">
        <div className="container mx-auto">
          {/* Header Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              App Marketplace
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              Connect with 100+ integrations to power your commerce operations.
              Discover apps to streamline workflows and grow your business.
            </p>

            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search integrations..."
                className="pl-12 h-12 text-lg"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-8">
            {/* Sidebar Navigation */}
            <aside className="w-64 flex-shrink-0 hidden lg:block">
              <div className="space-y-4">
                {/* Built By Filter */}
                <Card className="p-4 hidden">
                  <h3 className="font-semibold text-lg mb-4">Built By</h3>
                  <nav className="space-y-1">
                    <button
                      onClick={() => setSelectedBuilder("All")}
                      className={cn(
                        "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors",
                        selectedBuilder === "All"
                          ? "bg-primary text-primary-foreground"
                          : "hover:bg-muted"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <Grid className="h-4 w-4" />
                        <span>All</span>
                      </div>
                    </button>
                    <button
                      onClick={() => setSelectedBuilder("Ommni")}
                      className={cn(
                        "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors",
                        selectedBuilder === "Ommni"
                          ? "bg-primary text-primary-foreground"
                          : "hover:bg-muted"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        <span>Built by Ommni</span>
                      </div>
                    </button>
                    <button
                      onClick={() => setSelectedBuilder("Partner")}
                      className={cn(
                        "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors",
                        selectedBuilder === "Partner"
                          ? "bg-primary text-primary-foreground"
                          : "hover:bg-muted"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        <span>Built by Partner</span>
                      </div>
                    </button>
                  </nav>
                </Card>

                {/* Categories Filter */}
                <Card className="p-4">
                  <h3 className="font-semibold text-lg mb-4">Categories</h3>
                  <nav className="space-y-1">
                    <button
                      onClick={() => setSelectedCategory("All")}
                      className={cn(
                        "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors",
                        selectedCategory === "All"
                          ? "bg-primary text-primary-foreground"
                          : "hover:bg-muted"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <Grid className="h-4 w-4" />
                        <span>All Integrations</span>
                      </div>
                    </button>
                    {categories.map((category) => {
                      const Icon = categoryIcons[category] || Package;
                      return (
                        <button
                          key={category}
                          onClick={() => setSelectedCategory(category)}
                          className={cn(
                            "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors",
                            selectedCategory === category
                              ? "bg-primary text-primary-foreground"
                              : "hover:bg-muted"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            <span>{category}</span>
                          </div>
                        </button>
                      );
                    })}
                  </nav>
                </Card>
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1">
              {/* Mobile Filter Button */}
              <div className="mb-6 lg:hidden">
                <Sheet open={mobileFilterOpen} onOpenChange={setMobileFilterOpen}>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="w-full">
                      <SlidersHorizontal className="h-4 w-4 mr-2" />
                      Filter Integrations
                      {selectedCategory !== "All" && (
                        <Badge variant="secondary" className="ml-2">1</Badge>
                      )}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="bottom" className="h-[80vh] overflow-y-auto">
                    <SheetHeader>
                      <SheetTitle>Filter Integrations</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6 space-y-6">
                      <div>
                        <h3 className="font-semibold text-lg mb-4">Categories</h3>
                        <nav className="space-y-2">
                          <button
                            onClick={() => {
                              setSelectedCategory("All");
                              setMobileFilterOpen(false);
                            }}
                            className={cn(
                              "w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm transition-colors",
                              selectedCategory === "All"
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted hover:bg-muted/80"
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <Grid className="h-4 w-4" />
                              <span>All Integrations</span>
                            </div>
                          </button>
                          {categories.map((category) => {
                            const Icon = categoryIcons[category] || Package;
                            return (
                              <button
                                key={category}
                                onClick={() => {
                                  setSelectedCategory(category);
                                  setMobileFilterOpen(false);
                                }}
                                className={cn(
                                  "w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm transition-colors",
                                  selectedCategory === category
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted hover:bg-muted/80"
                                )}
                              >
                                <div className="flex items-center gap-2">
                                  <Icon className="h-4 w-4" />
                                  <span>{category}</span>
                                </div>
                              </button>
                            );
                          })}
                        </nav>
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
            {filteredIntegrations.map((integration, index) => (
              <Card
                key={index}
                className="group relative overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 hover:border-primary/50 bg-card cursor-pointer"
                onClick={() => {
                  trackIntegrationView(integration.name);
                  if (integration.link) {
                    window.location.href = integration.link;
                  }
                }}
              >
                <div className="p-6">
                  <div className="absolute top-4 right-4 flex flex-col gap-2 items-end">
                    {integration.popular && (
                      <Badge className="bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20">
                        <Zap className="w-3 h-3 mr-1" />
                        Popular
                      </Badge>
                    )}
                    <Badge 
                      variant={integration.builtBy === "Ommni" ? "default" : "secondary"}
                      className="text-xs hidden"
                    >
                      {integration.builtBy === "Ommni" ? (
                        <><Building2 className="w-3 h-3 mr-1" /> Ommni</>
                      ) : (
                        <><Users className="w-3 h-3 mr-1" /> Partner</>
                      )}
                    </Badge>
                  </div>
                  
                  <div className="w-16 h-16 mb-4 flex items-center justify-center bg-muted/50 rounded-xl p-3">
                    <img
                      src={integration.logo}
                      alt={`${integration.name} logo`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <h3 className="text-lg font-bold mb-1 group-hover:text-primary transition-colors">
                      {integration.name}
                    </h3>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {integration.category.map((cat) => (
                        <Badge key={cat} variant="outline" className="text-xs">
                          {cat}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {integration.description}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <Card className="p-12 text-center gradient-card">
            <h2 className="text-3xl font-bold mb-4">Need a Custom Integration?</h2>
            <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
              Our team can build custom integrations tailored to your specific needs.
              Connect any system to your commerce operations.
            </p>
            <Button variant="cta" size="lg" asChild>
              <Link to="/contact">Contact Our Team</Link>
            </Button>
          </Card>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Integrations;
